/**
 * Feature Flag Configuration Module
 * 
 * Provides a hosting-agnostic feature flag system using environment variables.
 * Environment variables use VITE_ prefix (required by Vite), but code uses clean names.
 */

/**
 * Feature flag enumeration for type-safe feature checking
 */
export enum FeatureFlag {
  // Admin Features
  ADMIN_PANEL = 'ADMIN_PANEL',
  ADMIN_IAM = 'ADMIN_IAM',
  ADMIN_AI_USAGE = 'ADMIN_AI_USAGE',
  ADMIN_AGENTS = 'ADMIN_AGENTS',
  ADMIN_KNOWLEDGE_CENTER = 'ADMIN_KNOWLEDGE_CENTER',
  ADMIN_ASSISTANTS = 'ADMIN_ASSISTANTS',
  ADMIN_CONVERSATION_HISTORY = 'ADMIN_CONVERSATION_HISTORY',
  ADMIN_CONVERSATION_FEEDBACK = 'ADMIN_CONVERSATION_FEEDBACK',

  // Chat Features
  CHAT_PROFILES = 'CHAT_PROFILES',
  CHAT_FILE_UPLOAD = 'CHAT_FILE_UPLOAD',
  CHAT_SHARE = 'CHAT_SHARE',
  CHAT_SETTINGS = 'CHAT_SETTINGS',

  // Header Features
  HEADER_API_KEYS = 'HEADER_API_KEYS',
  HEADER_README = 'HEADER_README',
  HEADER_THEME_TOGGLE = 'HEADER_THEME_TOGGLE',
}

/**
 * Parse environment variable value to boolean
 * Supports: true/false, 1/0, yes/no, on/off (case insensitive)
 * Default: true (if undefined or invalid)
 */
function parseBooleanEnv(value: string | undefined, defaultValue = true): boolean {
  if (value === undefined) return defaultValue;
  
  const normalized = value.toLowerCase().trim();
  
  if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === 'off') {
    return false;
  }
  
  if (normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on') {
    return true;
  }
  
  return defaultValue;
}

/**
 * Abstraction layer - isolates VITE_ prefixes to this file only
 * All feature flags with clean, hosting-agnostic names
 */
export const Features = {
  // Admin Features
  ADMIN_PANEL: parseBooleanEnv(import.meta.env.VITE_FEATURE_ADMIN_PANEL),
  ADMIN_IAM: parseBooleanEnv(import.meta.env.VITE_FEATURE_ADMIN_IAM),
  ADMIN_AI_USAGE: parseBooleanEnv(import.meta.env.VITE_FEATURE_ADMIN_AI_USAGE),
  ADMIN_AGENTS: parseBooleanEnv(import.meta.env.VITE_FEATURE_ADMIN_AGENTS),
  ADMIN_KNOWLEDGE_CENTER: parseBooleanEnv(import.meta.env.VITE_FEATURE_ADMIN_KNOWLEDGE_CENTER),
  ADMIN_ASSISTANTS: parseBooleanEnv(import.meta.env.VITE_FEATURE_ADMIN_ASSISTANTS),
  ADMIN_CONVERSATION_HISTORY: parseBooleanEnv(import.meta.env.VITE_FEATURE_ADMIN_CONVERSATION_HISTORY),
  ADMIN_CONVERSATION_FEEDBACK: parseBooleanEnv(import.meta.env.VITE_FEATURE_ADMIN_CONVERSATION_FEEDBACK),
  
  // Chat Features
  CHAT_PROFILES: parseBooleanEnv(import.meta.env.VITE_FEATURE_CHAT_PROFILES),
  CHAT_FILE_UPLOAD: parseBooleanEnv(import.meta.env.VITE_FEATURE_CHAT_FILE_UPLOAD),
  CHAT_SHARE: parseBooleanEnv(import.meta.env.VITE_FEATURE_CHAT_SHARE),
  CHAT_SETTINGS: parseBooleanEnv(import.meta.env.VITE_FEATURE_CHAT_SETTINGS),
  
  // Header Features
  HEADER_API_KEYS: parseBooleanEnv(import.meta.env.VITE_FEATURE_HEADER_API_KEYS),
  HEADER_README: parseBooleanEnv(import.meta.env.VITE_FEATURE_HEADER_README),
  HEADER_THEME_TOGGLE: parseBooleanEnv(import.meta.env.VITE_FEATURE_HEADER_THEME_TOGGLE),
} as const;

/**
 * Check if a feature flag is enabled
 * 
 * @param flag - Feature flag to check
 * @returns true if feature is enabled, false otherwise
 * 
 * @example
 * if (isFeatureEnabled(FeatureFlag.ADMIN_IAM)) {
 *   // Render IAM dashboard
 * }
 */
export function isFeatureEnabled(flag: FeatureFlag): boolean {
  // If the admin panel is disabled globally, all admin sub-features are also disabled
  if (flag.startsWith('ADMIN_') && !Features.ADMIN_PANEL) {
    return false;
  }
  
  const enabled = Features[flag as keyof typeof Features] ?? true;
  return enabled;
}

/**
 * Get all enabled features
 * 
 * @returns Set of enabled feature flags
 */
export function getEnabledFeatures(): Set<FeatureFlag> {
  const enabled = new Set<FeatureFlag>();

  for (const flag of Object.values(FeatureFlag)) {
    if (isFeatureEnabled(flag)) {
      enabled.add(flag);
    }
  }

  return enabled;
}

/**
 * Type guard to check if admin panel is enabled
 * Convenience helper for common check
 */
export function isAdminEnabled(): boolean {
  const enabled = Features.ADMIN_PANEL;
  console.debug(`[FeatureFlags] Admin Panel Enabled: ${enabled}`);
  return enabled;
}

/**
 * Get current feature flag values as a plain object
 * Useful for debugging or displaying current configuration
 */
export function getFeatureConfig() {
  return { ...Features };
}
